/**
 * CORNER LAPS (2026-10-13, Matt): "a warning when two different slopes come together at an
 * inside corner … with an option: 1 Set the shallow eave overhang depth to allow the fascias
 * and gutters to line up, 2 allow the steeper slope eave to tuck under the shallow slope's
 * overhang back to the wall it bears on, 3 ignore" — and the same for outside corners.
 *
 * Two sloped eaves meeting at a corner spring from the same plate, so with the same overhang
 * the steeper one ends LOWER: its fascia and gutter sit below the shallow side's. At an
 * INSIDE corner the shallow surface is the higher one out in the overhang, so it covers the
 * corner and the steeper eave stops short where the two surfaces meet (the steeper roof laps
 * under — it can never lap over). At an OUTSIDE corner (a hip) the hip line misses the eave
 * corner and the two eaves step.
 *
 * The corner's choice lives on the boundary node at the corner, as `edge.cornerLap` of the
 * edge that STARTS there: blank = unresolved (warn), 'tuck' (inside corners only), 'ignore'.
 * "Line up" is not stored — it writes the shallow edge's overhang, and the corner then
 * simply matches.
 */
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import type { ResolvedRoofSolve, RoofFace } from './solve'

/** eave tops closer than this read as lined up (1/8") */
const MATCH_FT = 1 / 96

export interface CornerLap {
  /** the boundary node at the corner (its edge is the one leaving the corner) */
  nodeId: string
  /** ring index of the corner vertex */
  vertex: number
  kind: 'inside' | 'outside'
  /** input-order edge indices meeting there */
  shallowEdge: number
  steepEdge: number
  /** how far apart the two eave tops (fascia/gutter line) sit, ft (+ = shallow higher) */
  dzFt: number
  /** the shallow edge's OVERHANG SETTING (the per-edge value, wall reach excluded) that
   *  lines the two eave tops up — null when it would be negative */
  lineUpOverhangFt: number | null
  /** the stored choice */
  choice: 'tuck' | 'ignore' | null
}

const dot = (a: Pt2, b: Pt2) => a[0] * b[0] + a[1] * b[1]

function ringArea(r: Pt2[]): number {
  let s = 0
  for (let i = 0; i < r.length; i++) { const a = r[i], b = r[(i + 1) % r.length]; s += a[0] * b[1] - b[0] * a[1] }
  return s / 2
}

/** per edge: unit direction + INWARD normal, for either winding */
function edgeFrames(ring: Pt2[]): { d: Pt2; inw: Pt2 }[] {
  const ccw = ringArea(ring) > 0
  return ring.map((a, i) => {
    const b = ring[(i + 1) % ring.length]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    const d: Pt2 = [(b[0] - a[0]) / L, (b[1] - a[1]) / L]
    return { d, inw: ccw ? [-d[1], d[0]] : [d[1], -d[0]] }
  })
}

/** the line {p : n·p = c} as (n, c) — intersect two of them */
function meet(n1: Pt2, c1: number, n2: Pt2, c2: number): Pt2 | null {
  const det = n1[0] * n2[1] - n1[1] * n2[0]
  if (Math.abs(det) < 1e-12) return null
  return [(c1 * n2[1] - c2 * n1[1]) / det, (n1[0] * c2 - n2[0] * c1) / det]
}

function cornerChoice(el: ResolvedElement, vertex: number): 'tuck' | 'ignore' | null {
  const nodes = (el.params.boundary as { nodes?: { edge?: { cornerLap?: unknown } }[] } | null)?.nodes
  const v = nodes?.[vertex]?.edge?.cornerLap
  return v === 'tuck' || v === 'ignore' ? v : null
}

/**
 * Every corner of a solved roof where two sloped eaves meet with their eave tops more than
 * 1/8" apart (lined-up corners and corners touching a gable are not reported).
 */
export function roofCornerLaps(el: ResolvedElement, solved: ResolvedRoofSolve | null): CornerLap[] {
  if (!solved || solved.flat) return []
  const { ring, specs, zPlateFt } = solved
  const n = ring.length
  if (n < 3 || specs.length !== n) return []
  const t = el.assemblyThicknessFt || 0
  const frames = edgeFrames(ring)
  const ccw = ringArea(ring) > 0
  const reach = el.roofBearingWallFt ?? []
  const nodes = (el.params.boundary as { nodes?: { id: string }[] } | null)?.nodes ?? []
  // each edge's own bearing height (PER-EDGE BEARING HEIGHT)
  const plateOf = (i: number) => zPlateFt + (solved.plateOffsetFt?.[i] ?? 0)
  const top = (i: number, ov: number) => {
    const m = specs[i].slopeRise / 12
    return plateOf(i) - m * ov + t * Math.sqrt(1 + m * m)
  }
  const out: CornerLap[] = []
  for (let v = 0; v < n; v++) {
    const p = (v + n - 1) % n, c = v
    const sp = specs[p], sc = specs[c]
    if (sp.gable || sc.gable || !(sp.slopeRise > 0) || !(sc.slopeRise > 0)) continue
    // a straight run (collinear edges) is not a corner
    const turn = frames[p].d[0] * frames[c].d[1] - frames[p].d[1] * frames[c].d[0]
    if (Math.abs(turn) < 1e-6) continue
    const zp = top(p, sp.overhangFt), zc = top(c, sc.overhangFt)
    if (Math.abs(zp - zc) <= MATCH_FT) continue
    // left turn on a CCW ring (or right on a CW one) is an outside corner
    const kind: 'inside' | 'outside' = (turn > 0) === ccw ? 'outside' : 'inside'
    // shallow = the flatter slope; equal slopes with unequal overhangs: the higher eave
    let shallow: number, steep: number
    if (Math.abs(sp.slopeRise - sc.slopeRise) > 1e-9) [shallow, steep] = sp.slopeRise < sc.slopeRise ? [p, c] : [c, p]
    else [shallow, steep] = zp > zc ? [p, c] : [c, p]
    const zSteep = top(steep, specs[steep].overhangFt)
    const ms = specs[shallow].slopeRise / 12
    // shallow overhang (total, from the bearing line) whose eave top meets the steep one's
    const ovTotal = (plateOf(shallow) + t * Math.sqrt(1 + ms * ms) - zSteep) / ms
    const setting = ovTotal - (reach[shallow] ?? 0)
    out.push({
      nodeId: nodes[v]?.id ?? '', vertex: v, kind, shallowEdge: shallow, steepEdge: steep,
      dzFt: top(shallow, specs[shallow].overhangFt) - zSteep,
      lineUpOverhangFt: setting >= 0 ? setting : null,
      choice: cornerChoice(el, v),
    })
  }
  return out
}

/**
 * TUCK (inside corners set to 'tuck'; Matt: "allow the steeper slope eave to tuck under the
 * shallow slope's overhang back to the wall it bears on" — ending at the wall's FINISHED
 * face — and r2: "that wedge should be cropped as the final clean up").
 *
 * At the corner, with the steep eave line, the shallow eave line, the line where the two
 * underside planes meet (the valley) and the finished face of the wall under the shallow
 * side:
 *   Q  = steep eave ∩ valley   (where the steep eave used to stop)
 *   C  = steep eave ∩ shallow eave   (the eave ring corner)
 *   X  = shallow eave ∩ valley (where the shallow fascia dies into the steep roof)
 *   Vf = valley ∩ wall face,  T = steep eave ∩ wall face
 *   V  = the corner itself (valley ∩ both bearing lines)
 * Result:
 *   - the SHALLOW face is trimmed back to its own eave line (the wedge Q·C·X past its fascia
 *     is gone) and its valley edge is split at Vf;
 *   - the STEEP face takes over that wedge: its edge Q→V becomes C→X→Vf→V and its eave runs
 *     straight on to C;
 *   - a TUCKED piece [C, T, Vf, X] on the steep plane carries the eave (fascia + gutter) on
 *     under the shallow sheet to the wall face — `flying` (outside the plan partition) and
 *     `tucked` (the skeleton keeps it out of the shared-edge census);
 *   - override: C→X is the shallow EAVE (its fascia runs on to where it meets the steep roof)
 *     and joins the shallow eave run before it.
 */
export interface TuckResult {
  faces: RoofFace[]
  overrides: { a: Pt2; b: Pt2; kind: 'eave'; joinRun: boolean; edgeIndex: number }[]
}

const near = (p: Pt2, q: Pt2) => Math.hypot(p[0] - q[0], p[1] - q[1]) < 1e-5

/** in a ring, replace the edge p→q (either direction) with p→…mid…→q */
function replaceEdge(ring: Pt2[], p: Pt2, q: Pt2, mid: Pt2[]): Pt2[] | null {
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const a = ring[i], b = ring[(i + 1) % n]
    if (near(a, p) && near(b, q)) return [...ring.slice(0, i + 1), ...mid, ...ring.slice(i + 1)]
    if (near(a, q) && near(b, p)) return [...ring.slice(0, i + 1), ...[...mid].reverse(), ...ring.slice(i + 1)]
  }
  return null
}

/** insert point m into the ring edge whose segment contains it */
function insertOnEdge(ring: Pt2[], m: Pt2): Pt2[] {
  const n = ring.length
  if (ring.some((p) => near(p, m))) return ring
  for (let i = 0; i < n; i++) {
    const a = ring[i], b = ring[(i + 1) % n]
    const L = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (L < 1e-9) continue
    const cross = ((b[0] - a[0]) * (m[1] - a[1]) - (b[1] - a[1]) * (m[0] - a[0])) / L
    const t = ((m[0] - a[0]) * (b[0] - a[0]) + (m[1] - a[1]) * (b[1] - a[1])) / (L * L)
    if (Math.abs(cross) < 1e-6 && t > 1e-6 && t < 1 - 1e-6) return [...ring.slice(0, i + 1), m, ...ring.slice(i + 1)]
  }
  return ring
}

/** keep the part of a ring where n·p ≥ c (Sutherland–Hodgman against one line) */
function clipKeep(ring: Pt2[], n: Pt2, c: number): Pt2[] {
  const out: Pt2[] = []
  for (let i = 0; i < ring.length; i++) {
    const P = ring[i], Q = ring[(i + 1) % ring.length]
    const fp = dot(n, P) - c, fq = dot(n, Q) - c
    if (fp >= -1e-9) out.push(P)
    if ((fp >= -1e-9) !== (fq >= -1e-9)) {
      const t = fp / (fp - fq)
      out.push([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t])
    }
  }
  return out.filter((p, i) => !near(p, out[(i + out.length - 1) % out.length]))
}

export function applyCornerTucks(el: ResolvedElement, solved: ResolvedRoofSolve): TuckResult {
  const laps = roofCornerLaps(el, solved).filter((l) => l.kind === 'inside' && l.choice === 'tuck')
  const res: TuckResult = { faces: solved.faces, overrides: [] }
  if (!laps.length) return res
  const { ring, specs } = solved
  const frames = edgeFrames(ring)
  const faceReach = el.roofBearingWallFaceFt ?? el.roofBearingWallFt ?? []
  let faces = solved.faces.slice()
  for (const lap of laps) {
    const s = lap.steepEdge, h = lap.shallowEdge
    const pi = faces.findIndex((f) => f.edgeIndex === s && !f.flying)
    const hi = faces.findIndex((f) => f.edgeIndex === h && !f.flying)
    if (pi < 0 || hi < 0) continue
    const as = ring[s], ah = ring[h]
    const ins = frames[s].inw, inh = frames[h].inw
    const ms = specs[s].slopeRise / 12, mh = specs[h].slopeRise / 12
    const eaveS: [Pt2, number] = [ins, dot(ins, as) - specs[s].overhangFt]
    const eaveH: [Pt2, number] = [inh, dot(inh, ah) - specs[h].overhangFt]
    const wall: [Pt2, number] = [inh, dot(inh, ah) - (faceReach[h] ?? 0)]
    // where the two underside planes meet — read off the faces themselves, so each edge's
    // own bearing height counts (PER-EDGE BEARING HEIGHT): zs(p) − zh(p) = vn·p + k = 0
    const zs0 = faces[pi].zAt([0, 0]), zh0 = faces[hi].zAt([0, 0])
    const vn: Pt2 = [ms * ins[0] - mh * inh[0], ms * ins[1] - mh * inh[1]]
    const valley: [Pt2, number] = [vn, -(zs0 - zh0)]
    const Q = meet(eaveS[0], eaveS[1], valley[0], valley[1])
    const C = meet(eaveS[0], eaveS[1], eaveH[0], eaveH[1])
    const X = meet(eaveH[0], eaveH[1], valley[0], valley[1])
    const Vf = meet(valley[0], valley[1], wall[0], wall[1])
    const T = meet(eaveS[0], eaveS[1], wall[0], wall[1])
    if (!Q || !C || !X || !Vf || !T) continue
    const parent = faces[pi], shallow = faces[hi]
    // V: the steep face's other valley vertex next to Q — the drawn corner when both edges
    // bear at the plate; elsewhere when a PER-EDGE BEARING HEIGHT moved the valley (a raised
    // wing's valley crosses its own eave line out over the main roof)
    const onValley = (p: Pt2) => Math.abs(dot(valley[0], p) - valley[1]) <= 1e-6 * (Math.hypot(valley[0][0], valley[0][1]) + 1)
    const qi = parent.ring.findIndex((p) => near(p, Q))
    if (qi < 0) continue
    const nb = [parent.ring[(qi + 1) % parent.ring.length], parent.ring[(qi + parent.ring.length - 1) % parent.ring.length]]
    const V = nb.find((p) => onValley(p) && !near(p, Q))
    if (!V) continue
    // the order along the valley must be Q · X · Vf · V (the steep eave stops short of the
    // shallow eave line, which sits outside the wall face)
    const along = (p: Pt2) => dot(inh, p)
    if (!(along(Q) < along(X) - 1e-6 && along(X) < along(Vf) + 1e-6 && along(Vf) <= along(V) + 1e-6)) continue
    // steep face: Q→V becomes Q→C→X→Vf→V (Vf dropped when it sits on V), then the now
    // collinear Q on the eave line goes, so the eave runs straight on to C
    const midP: Pt2[] = near(Vf, V) ? [C, X] : [C, X, Vf]
    let pr = replaceEdge(parent.ring, Q, V, midP)
    if (!pr) continue
    pr = pr.filter((p) => !near(p, Q))
    // shallow face: trimmed to its own eave line, valley edge split at Vf
    let sr = clipKeep(shallow.ring, inh, eaveH[1])
    sr = insertOnEdge(sr, C)
    if (!near(Vf, V)) sr = insertOnEdge(sr, Vf)
    if (sr.length < 3 || Math.abs(ringArea(sr)) < 1e-6) continue
    // the tucked piece under the shallow sheet, wound like the steep face
    let piece: Pt2[] = near(Vf, V) ? [C, T, V, X] : [C, T, Vf, X]
    if ((ringArea(piece) > 0) !== (ringArea(pr) > 0)) piece = piece.reverse()
    if (Math.abs(ringArea(piece)) < 1e-6) continue
    faces[pi] = { ...parent, ring: pr }
    faces[hi] = { ...shallow, ring: sr }
    faces = [...faces, { ...parent, ring: piece, flying: true, tucked: true }]
    res.overrides.push({ a: C, b: X, kind: 'eave', joinRun: true, edgeIndex: h })
  }
  res.faces = faces
  return res
}

/** the tucked pieces alone (tests and callers that only need them) */
export function tuckedCornerFaces(el: ResolvedElement, solved: ResolvedRoofSolve): RoofFace[] {
  return applyCornerTucks(el, solved).faces.filter((f) => f.tucked)
}

/** the Properties/plan wording for one corner */
export function cornerLapText(lap: CornerLap): string {
  const inch = Math.round(Math.abs(lap.dzFt) * 12 * 8) / 8
  return `${lap.kind === 'inside' ? 'Inside' : 'Outside'} corner: side ${lap.shallowEdge + 1} and side ${lap.steepEdge + 1} eaves sit ${inch}" apart`
}

/** a plan point for a corner's warning marker */
export function cornerLapAt(solved: ResolvedRoofSolve, lap: CornerLap): Pt2 {
  return solved.ring[lap.vertex]
}

